'use client'

import React from 'react'
import { Search, Filter, Eye, Trash2 } from 'lucide-react'
import {
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle
} from "@/components/ui/card"
import {
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { 
  Avatar, 
  AvatarFallback, 
  AvatarImage 
} from "@/components/ui/avatar"

// Types (assuming these are passed via props or defined/imported)
interface BudgetItem {
  id: string;
  userId: string;
  description: string;
  merchantName: string;
  amount: number;
  date: string;
}

interface AggregatedBudgetItem extends BudgetItem {
  categoryName: string;
  categoryIcon: string;
}

interface Collaborator {
  id: string;
  name: string;
  initials: string;
  imageUrl: string | null;
}

// Helpers (assuming these are passed via props or defined/imported)
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2)
}

interface TransactionsTableCardProps {
  transactions: AggregatedBudgetItem[];
  collaboratorData: Collaborator[];
  searchQuery: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFilterClick: () => void;
  onViewTransaction: (id: string) => void;
  onDeleteTransaction: (id: string) => void;
}

export default function TransactionsTableCard({ 
  transactions, 
  collaboratorData, 
  searchQuery,
  onSearchChange,
  onFilterClick,
  onViewTransaction,
  onDeleteTransaction
}: TransactionsTableCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Recent Associated Transactions</CardTitle>
          <CardDescription>All recent transactions across "Needs" categories.</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative ml-auto flex-1 md:grow-0">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search transactions..."
              className="w-full rounded-lg bg-background pl-8 md:w-[200px] lg:w-[300px] h-9"
              value={searchQuery}
              onChange={onSearchChange}
            />
          </div>
          <Button 
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={onFilterClick}
          >
            <Filter className="h-4 w-4" />
            <span className="sr-only">Filter Transactions</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {transactions.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px] pl-2"></TableHead>
                <TableHead className="w-[80px] hidden sm:table-cell">Date</TableHead>
                <TableHead>Merchant</TableHead>
                <TableHead className="hidden md:table-cell">Category</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map(item => {
                const collaborator = collaboratorData.find(c => c.id === item.userId);
                
                return (
                  <TableRow key={item.id} className="odd:bg-muted/50">
                    <TableCell className="pl-2">
                      {collaborator && (
                        <Avatar className="h-6 w-6 border">
                          <AvatarImage src={collaborator.imageUrl ?? undefined} alt={collaborator.name} />
                          <AvatarFallback className="text-[10px]">{collaborator.initials}</AvatarFallback>
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-xs text-muted-foreground">{formatDate(item.date)}</TableCell>
                    <TableCell className="font-medium text-sm">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6 border">
                          <AvatarFallback className="text-[10px]">{getInitials(item.merchantName)}</AvatarFallback>
                        </Avatar>
                        <span>{item.merchantName}</span>
                      </div>
                      <div className="md:hidden text-xs text-muted-foreground pl-8">
                        {item.categoryIcon} {item.categoryName} - {formatDate(item.date)}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge variant="secondary" className="text-xs font-normal">
                        <span title={item.categoryName} className="mr-1">{item.categoryIcon}</span> 
                        {item.categoryName}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-sm">{formatCurrency(item.amount)}</TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7 mr-1" 
                        onClick={() => onViewTransaction(item.id)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7 text-destructive hover:text-destructive/90" 
                        onClick={() => onDeleteTransaction(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-muted-foreground">No transactions match your search or none recorded for this period.</p>
        )}
      </CardContent>
    </Card>
  );
} 